import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowDown,
  FaCalendarAlt,
  FaChartBar,
  FaChevronLeft,
  FaChevronRight,
  FaGift,
  FaUserCheck,
  FaUsers,
} from "react-icons/fa";
import "./HeaderSlider.css";

const slides = [
  {
    src: "/img/cover/illustration_hero01.webp",
    title: "Présences en un clic",
    subtitle: "Un pointage rapide et clair",
    description:
      "Suivez les présents, absents et malades sur vos groupes avec une interface simple et efficace.",
    bgColor: "from-emerald-500 to-green-600",
    icon: <FaUserCheck className="text-white text-5xl" />,
  },
  {
    src: "/img/cover/illustration_hero01.webp",
    title: "Votre club mieux organisé",
    subtitle: "Lieux, groupes, enseignants, adhérents",
    description:
      "Importez facilement vos données et accédez à une vue d'ensemble claire pour mieux piloter votre club.",
    bgColor: "from-blue-500 to-sky-600",
    icon: <FaUsers className="text-white text-5xl" />,
  },
  {
    src: "/img/cover/illustration_hero01.webp",
    title: "Des séances mieux préparées",
    subtitle: "Gagnez du temps à chaque cours",
    description:
      "Possibilité d'inviter les enseignants à se connecter gratuitement à l'application.",
    bgColor: "from-indigo-500 to-blue-700",
    icon: <FaCalendarAlt className="text-white text-5xl" />,
  },
  {
    src: "/img/cover/illustration_hero01.webp",
    title: "Des statistiques utiles",
    subtitle: "Pilotez votre activité plus facilement",
    description:
      "Obtenez une vision claire de la fréquentation et de la dynamique du club grâce à des indicateurs lisibles.",
    bgColor: "from-violet-500 to-indigo-700",
    icon: <FaChartBar className="text-white text-5xl" />,
  },
];

function HomeHeroSection({ isConnected = false }) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const navigate = useNavigate();

  const nextSlide = () => {
    setCurrentSlideIndex((prevIndex) => (prevIndex + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlideIndex((prevIndex) =>
      prevIndex === 0 ? slides.length - 1 : prevIndex - 1
    );
  };

  const scrollToNextSection = () => {
    const nextSection = document.getElementById("home-content");
    if (nextSection) {
      window.scrollTo({
        top: nextSection.offsetTop - 90,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const autoSlide = setInterval(nextSlide, 10000);
    return () => clearInterval(autoSlide);
  }, []);

  const currentSlide = slides[currentSlideIndex];

  return (
    <header className="relative flex min-h-[100vh] items-center overflow-hidden bg-black">
      {slides.map((slide, index) => (
        <img
          key={index}
          src={slide.src}
          alt={`Slide ${index + 1}`}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
            index === currentSlideIndex ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}

      <div className="absolute inset-0 bg-slate-950/65" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.18),transparent_25%),radial-gradient(circle_at_bottom_left,rgba(16,185,129,0.18),transparent_25%)]" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-20 sm:px-6">
        <div className="max-w-3xl">
          <div className="mb-4 flex flex-wrap gap-2">
            <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white">
              Application web pour clubs sportifs
            </span>

            {isConnected ? (
              <span className="inline-flex rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-xs font-semibold text-emerald-200">
                Compte connecté
              </span>
            ) : (
              <span className="inline-flex rounded-full border border-blue-400/20 bg-blue-400/10 px-3 py-1 text-xs font-semibold text-blue-200">
                Découverte gratuite
              </span>
            )}
          </div>

          <h1 className="text-4xl font-black leading-tight text-white sm:text-5xl lg:text-6xl">
            Gérez votre club et suivez les présences en toute simplicité
          </h1>

          <p className="mt-5 max-w-2xl text-sm leading-7 text-white/80 sm:text-base">
            Présence Club Pro vous aide à organiser les lieux, les groupes,
            les enseignants et les adhérents, puis à suivre les présences
            séance après séance.
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            {isConnected ? (
              <>
                <button
                  onClick={() => navigate("/gestion-presences")}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                >
                  <FaUserCheck />
                  Aller à la gestion des présences
                </button>

                <button
                  onClick={() => navigate("/formules-tarifs")}
                  className="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/10 px-5 py-3 font-semibold text-white transition hover:bg-white/15"
                >
                  Voir les formules
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate("/register")}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600"
                >
                  <FaGift />
                  Découvrir la démo
                </button>

                <button
                  onClick={() => navigate("/formules-tarifs")}
                  className="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/10 px-5 py-3 font-semibold text-white transition hover:bg-white/15"
                >
                  Voir les formules
                </button>
              </>
            )}
          </div>

          <div
            className={`mt-10 flex max-w-[560px] flex-col gap-4 rounded-[28px] border border-white/10 bg-gradient-to-br ${currentSlide.bgColor} p-6 shadow-2xl backdrop-blur-sm sm:flex-row sm:items-center`}
          >
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-3xl bg-white/10">
              {currentSlide.icon}
            </div>

            <div className="min-w-0">
              <p className="text-sm font-semibold uppercase tracking-wider text-white/70">
                Offre mise en avant
              </p>
              <h2 className="mt-1 text-2xl font-black text-white">
                {currentSlide.title}
              </h2>
              <h3 className="mt-1 text-base font-semibold text-white/90">
                {currentSlide.subtitle}
              </h3>
              <p className="mt-3 text-sm leading-6 text-white/85">
                {currentSlide.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-24 z-20 hidden justify-between px-6 md:flex">
        <button
          onClick={prevSlide}
          className="rounded-full bg-slate-900/70 p-4 text-white transition hover:bg-slate-900"
        >
          <FaChevronLeft className="text-2xl" />
        </button>
        <button
          onClick={nextSlide}
          className="rounded-full bg-slate-900/70 p-4 text-white transition hover:bg-slate-900"
        >
          <FaChevronRight className="text-2xl" />
        </button>
      </div>

      <div className="absolute bottom-6 z-20 flex w-full justify-center">
        <button
          onClick={scrollToNextSection}
          className="animate-bounce text-white flex flex-col items-center"
        >
          <FaArrowDown className="mb-2 text-3xl" />
          <span className="text-sm font-medium">Découvrir</span>
        </button>
      </div>
    </header>
  );
}

export default HomeHeroSection;